// IndexedDB utility for persisting uploaded files across navigation

const DB_NAME = "shoebox-upload-storage";
const DB_VERSION = 1;
const STORE_NAME = "uploaded-files";

interface StoredFileInfo {
  id: string;
  fileName: string;
  fileData: ArrayBuffer;
  fileType: string;
  order: any | null;
  processing: boolean;
  error: string | null;
}

// Open or create the IndexedDB database
function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "id" });
      }
    };
  });
}

// Save a file to IndexedDB
export async function saveFileToStorage(
  id: string,
  file: File,
  order: any | null,
  processing: boolean,
  error: string | null = null
): Promise<void> {
  const db = await openDB();
  const arrayBuffer = await file.arrayBuffer();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, "readwrite");
    const store = transaction.objectStore(STORE_NAME);

    const storedFile: StoredFileInfo = {
      id,
      fileName: file.name,
      fileData: arrayBuffer,
      fileType: file.type,
      order,
      processing,
      error,
    };

    const request = store.put(storedFile);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve();

    transaction.oncomplete = () => db.close();
  });
}

// Update order data for a file in IndexedDB
export async function updateFileOrderInStorage(
  id: string,
  order: any,
  processing: boolean,
  error: string | null = null
): Promise<void> {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, "readwrite");
    const store = transaction.objectStore(STORE_NAME);

    const getRequest = store.get(id);
    getRequest.onsuccess = () => {
      const existingFile = getRequest.result as StoredFileInfo | undefined;
      if (existingFile) {
        existingFile.order = order;
        existingFile.processing = processing;
        existingFile.error = error;
        const putRequest = store.put(existingFile);
        putRequest.onerror = () => reject(putRequest.error);
        putRequest.onsuccess = () => resolve();
      } else {
        resolve(); // File not found, nothing to update
      }
    };
    getRequest.onerror = () => reject(getRequest.error);

    transaction.oncomplete = () => db.close();
  });
}

// Get all files from IndexedDB
export async function getFilesFromStorage(): Promise<
  Array<{
    id: string;
    file: File;
    order: any | null;
    previewUrl: string;
    fileName: string;
    processing: boolean;
    error: string | null;
  }>
> {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, "readonly");
    const store = transaction.objectStore(STORE_NAME);
    const request = store.getAll();

    request.onerror = () => reject(request.error);
    request.onsuccess = () => {
      const storedFiles = request.result as StoredFileInfo[];
      const files = storedFiles.map((storedFile) => {
        // Reconstruct File object from ArrayBuffer
        const blob = new Blob([storedFile.fileData], {
          type: storedFile.fileType,
        });
        const file = new File([blob], storedFile.fileName, {
          type: storedFile.fileType,
        });

        // Create new preview URL
        const previewUrl = URL.createObjectURL(file);

        return {
          id: storedFile.id,
          file,
          order: storedFile.order,
          previewUrl,
          fileName: storedFile.fileName,
          processing: storedFile.processing,
          error: storedFile.error || null,
        };
      });
      resolve(files);
    };

    transaction.oncomplete = () => db.close();
  });
}

// Count files that were uploaded but not yet added to the library
export async function countFilesInStorage(): Promise<number> {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, "readonly");
    const store = transaction.objectStore(STORE_NAME);
    const request = store.count();

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    transaction.oncomplete = () => db.close();
  });
}

// Remove a file from IndexedDB
export async function removeFileFromStorage(id: string): Promise<void> {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, "readwrite");
    const store = transaction.objectStore(STORE_NAME);
    const request = store.delete(id);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve();

    transaction.oncomplete = () => db.close();
  });
}

// Clear all files from IndexedDB
export async function clearAllFilesFromStorage(): Promise<void> {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, "readwrite");
    const store = transaction.objectStore(STORE_NAME);
    const request = store.clear();

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve();

    transaction.oncomplete = () => db.close();
  });
}
